import React, { useEffect, useState } from 'react';
import { adminApi, formatApiError } from './adminApi';
import {
  Activity,
  Send,
  Calendar,
  Users,
  Inbox,
  Trophy,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  FileText,
} from 'lucide-react';

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-[#15151A] border border-white/5 rounded-2xl p-5">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-4 ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
    <p className="text-white text-2xl font-light font-['Inter']">{value}</p>
    <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mt-1">{label}</p>
  </div>
);

const AdminDigest = () => {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [days, setDays] = useState(7);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = async (overrideDays = days) => {
    setLoading(true);
    try {
      const [s, p, h] = await Promise.all([
        adminApi.digestStatus(),
        adminApi.digestPreview(overrideDays),
        adminApi.digestHistory(),
      ]);
      setStatus(s);
      setPreview(p);
      setHistory(h);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendNow = async () => {
    setError('');
    setSuccess('');
    setSending(true);
    try {
      const res = await adminApi.digestSend(days);
      setSuccess(`Sent to ${res.recipients?.length || 0} recipients with ${res.leads_count} leads in scope.`);
      refresh(days);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-10 max-w-5xl" data-testid="admin-digest-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Reporting</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Weekly Digest</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            One email a week summarising leads, deal pulse, audience growth and content shipped.
          </p>
        </div>
        <button
          onClick={() => refresh(days)}
          data-testid="digest-refresh"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {status && (
        <div
          data-testid="digest-status-banner"
          className={`flex items-start gap-3 px-5 py-4 rounded-xl mb-6 border ${
            status.configured
              ? 'bg-[#83BF42]/5 border-[#83BF42]/20 text-[#83BF42]'
              : 'bg-amber-500/5 border-amber-500/20 text-amber-200'
          }`}
        >
          <Calendar className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm font-['Lato'] flex-1">
            <strong className="font-['Inter'] mr-1">{status.schedule}.</strong>
            {status.configured ? (
              <>
                Sent to <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs">{status.recipients.join(', ')}</code>
                {status.next_run_at && (
                  <span className="text-white/50">
                    {' · '}Next run: {new Date(status.next_run_at).toLocaleString()}
                  </span>
                )}
              </>
            ) : (
              <>RESEND_API_KEY isn't configured — automatic sends are paused.</>
            )}
          </div>
        </div>
      )}

      {/* Preview stats */}
      {preview && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-base font-medium font-['Inter']">Activity preview</h2>
            <div className="flex gap-1.5" data-testid="digest-window-picker">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  onClick={async () => { setDays(d); await refresh(d); }}
                  data-testid={`digest-window-${d}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-['Inter'] transition ${
                    days === d ? 'bg-[#83BF42] text-[#0F0F11]' : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
                >
                  {d} days
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat icon={Inbox} label="New leads" value={preview.leads_count} accent="bg-[#83BF42]/10 text-[#83BF42]" />
            <Stat icon={Users} label="Subscribers" value={preview.subscribers_count} accent="bg-blue-500/10 text-blue-400" />
            <Stat icon={Trophy} label="Won" value={preview.won_count} accent="bg-emerald-500/10 text-emerald-400" />
            <Stat icon={Trophy} label="Lost" value={preview.lost_count} accent="bg-red-500/10 text-red-400" />
            <Stat icon={FileText} label="Articles" value={preview.articles_count} accent="bg-purple-500/10 text-purple-400" />
            <Stat icon={Activity} label="Cases / Broadcasts" value={`${preview.cases_count}/${preview.broadcasts_count}`} accent="bg-orange-500/10 text-orange-400" />
          </div>
        </div>
      )}

      {/* Send now */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8 mb-8">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-1 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#83BF42]" /> Send now
        </h2>
        <p className="text-white/40 text-xs font-['Lato'] mb-5">
          Manually deliver the digest using the currently selected window ({days} days).
        </p>

        {error && (
          <div data-testid="digest-error" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div data-testid="digest-success" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {success}
          </div>
        )}

        <button
          onClick={handleSendNow}
          disabled={sending || !status?.configured}
          data-testid="digest-send-now"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          {sending ? 'Sending…' : `Send ${days}-day digest now`}
        </button>
      </div>

      {/* History */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-5">History</h2>
        {loading ? (
          <p className="text-white/40 text-sm font-['Lato'] py-8 text-center">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-white/40 text-sm font-['Lato']" data-testid="digest-history-empty">
            No digests sent yet.
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {history.map((h) => (
              <li key={h.id} className="py-4" data-testid={`digest-history-${h.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-medium font-['Inter']">{h.subject}</p>
                    <p className="text-white/50 text-xs font-['Lato'] mt-1">
                      {h.leads_count} leads · {h.subscribers_count} new subs · {h.won_count} won / {h.lost_count} lost ·{' '}
                      {h.articles_count} articles · {h.broadcasts_count} broadcasts
                    </p>
                    <p className="text-white/30 text-xs font-['Lato'] mt-2">
                      Sent to {h.recipients?.join(', ')} · {h.created_at ? new Date(h.created_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-['Inter'] border ${
                      h.status === 'sent'
                        ? 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30'
                        : 'bg-red-500/15 text-red-300 border-red-500/30'
                    }`}
                  >
                    {h.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDigest;
